import { useContext, PropsWithChildren } from 'react';
import { Message } from '@scale/shared/types/chat-completion';
import { ChatMessageDisplayType, MessageRole } from '@scale/shared/types/chat';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import TurnActions from 'react-scale/components/scalers/tasks/ThreadedInteraction/TurnActions';
import {
  useChatStoreExtract,
  useHasIncompleteStepsInTurn,
  useInvalidErrorMessagesForTurn,
  useIsRealCustomerAudit,
} from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.hooks';
import _ from 'lodash';
import { ChatMarkdown } from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/ChatMarkdown';
import { useConfigureChatMessageDisplay } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useConfigureChatMessageDisplay';
import { StepCard } from './step/StepCard';
import { ChatMessageDisplayConfigButton } from './step/ChatMessageDisplayConfig';
import clsx from 'clsx';
import { Icon } from '@scale/scaleui';
import { ChatIFrame } from './ChatIFrame';
import { twMerge } from 'tailwind-merge';
// import { ChatLazarusUIRenderer } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ChatLazarusUIRenderer';

type Props = PropsWithChildren<{
  className?: string;
  message: Message;
  onClick?: () => void;
  turnIndex: number;
  totalTurns: number;
  isAnyTurnIncomplete: boolean;
  expanded?: boolean;
  enableRTLText?: boolean;
  readOnly?: boolean;
  hideMessageWhenExpanded?: boolean;
  disableExpand?: boolean;
}>;

// eslint-disable-next-line complexity
export function ExpandableChatMessage({
  className,
  message,
  onClick,
  turnIndex,
  totalTurns,
  isAnyTurnIncomplete,
  expanded = false,
  enableRTLText,
  hideMessageWhenExpanded = false,
  readOnly,
  children,
  disableExpand = false, // disables the ability to expand the chat history
}: Props) {
  const taskContext = useContext(ChatTaskContext);

  const config = useChatStoreExtract(state => state.config);

  const {
    chatMessageDisplayConfig,
    useMarkdownDisplay,
    updateMarkdownConfig,
    toggleChatMessageDisplayType,
  } = useConfigureChatMessageDisplay();

  const hasIncompletedSteps = useHasIncompleteStepsInTurn(turnIndex);
  const invalidErrorMessages = useInvalidErrorMessagesForTurn(turnIndex);
  const isRealCustomerAudit = useIsRealCustomerAudit();

  const isTurnInvalid =
    !isRealCustomerAudit && (hasIncompletedSteps || invalidErrorMessages.length > 0);

  const isMessageVisible = !(expanded && hideMessageWhenExpanded);

  return (
    <div
      className={twMerge(
        'relative',
        taskContext.enableUnifiedFeedback
          ? 'rounded-lg hover:outline hover:outline-2 hover:outline-primary-200 focus:ring-2 focus:ring-primary-100'
          : '',
        taskContext.enableUnifiedFeedback && isTurnInvalid
          ? 'outline outline-danger-100 outline-2 hover:outline-danger-500'
          : '',
      )}
    >
      <StepCard className={className}>
        <StepCard.Header
          className={clsx('bg-neutral-100', {
            'cursor-pointer': !expanded || !hasIncompletedSteps,
          })}
          onClick={onClick}
        >
          <StepCard.HeaderLeft>
            {message.role === MessageRole.User ? (
              <StepCard.Icon icon="user" />
            ) : (
              <StepCard.Image src="https://static.remotasks.com/uploads/602b25a6e0984c00343d3b26/Frame.png" />
            )}
            {!_.isNil(turnIndex) && (
              <StepCard.HeaderColumn>
                <StepCard.Title className="inline">
                  Turn #{turnIndex! + 1}
                  {message.role === MessageRole.User ? ' - Prompt' : ' - Response'}
                </StepCard.Title>
                {isTurnInvalid && (
                  <StepCard.Subtitle className="inline">
                    <div className="flex flex-row items-center">
                      <Icon icon={['far', 'exclamation-circle']} size="sm" />
                      {invalidErrorMessages.length > 0
                        ? invalidErrorMessages[0]
                        : 'This turn has some steps that are not completed.'}
                    </div>
                  </StepCard.Subtitle>
                )}
              </StepCard.HeaderColumn>
            )}
          </StepCard.HeaderLeft>
          <StepCard.HeaderRight>
            {!expanded && (
              <>
                <StepCard.Button
                  tooltip="Copy this chat message"
                  icon="copy"
                  onClick={e => {
                    e.stopPropagation();
                    navigator.clipboard.writeText(message.content);
                  }}
                />
                <ChatMessageDisplayConfigButton
                  chatMessageDisplayConfig={chatMessageDisplayConfig}
                  useMarkdownDisplay={useMarkdownDisplay}
                  updateMarkdownConfig={updateMarkdownConfig}
                  toggleChatMessageDisplayType={toggleChatMessageDisplayType}
                />
                <TurnActions
                  readOnly={readOnly}
                  turnIndex={turnIndex}
                  totalTurns={totalTurns}
                  isAnyTurnIncomplete={isAnyTurnIncomplete}
                />
              </>
            )}
            {expanded ? (
              <StepCard.Button
                onClick={e => {
                  e.stopPropagation();
                  onClick?.();
                }}
                tooltip="Hide step history"
                icon="chevron-up"
                disabled={hasIncompletedSteps}
              />
            ) : (
              <StepCard.Button
                onClick={e => {
                  e.stopPropagation();
                  onClick?.();
                }}
                tooltip="Expand and show step history"
                icon="chevron-down"
                disabled={disableExpand}
                data-testid="expand-chat-message"
              />
            )}
          </StepCard.HeaderRight>
        </StepCard.Header>
        <StepCard.Body
          className={clsx('py-4 pb-0 border-neutral-100 bg-neutral-100 rounded-b-lg', {
            'cursor-pointer': !expanded,
          })}
          onClick={expanded ? undefined : onClick}
        >
          {isMessageVisible && (
            <div className="flex pb-4 flex-col flex-1 w-full">
              <div className="flex-1 w-full">
                {chatMessageDisplayConfig.displayType === ChatMessageDisplayType.Markdown ? (
                  <div className="w-full">
                    {config.renderCode && <ChatIFrame src={message.content} />}
                    {/* TODO @dannyadkins @victorlee hide this dynamically */}
                    {/* <ChatLazarusUIRenderer src={message.content} /> */}
                    <ChatMarkdown
                      enableRTL={enableRTLText}
                      markdown={message.content}
                      enableMath={chatMessageDisplayConfig.markdownConfig.enableMathPlugin}
                      enableSingleDollarTextMath={
                        chatMessageDisplayConfig.markdownConfig.enableSingleDollarTextMath
                      }
                      enableHtml={taskContext.htmlMarkdownConfig?.enablePromptPreviews}
                      enableSoftBreaks={taskContext.generalMarkdownConfig?.enableSoftLineBreaks}
                      enableExecutableCodeBlocks={
                        taskContext.codingConfig?.enableExecutableCodeMarkdown ??
                        taskContext.generalMarkdownConfig?.enableExecutableCodeMarkdown
                      }
                      defaultExpandCodeBlocks={false}
                    />
                  </div>
                ) : (
                  <div className="text-neutral-900 text-sm whitespace-pre-wrap">
                    {message.content}
                  </div>
                )}
              </div>
            </div>
          )}
          {expanded && <div className="flex flex-col w-full items-center gap-4">{children}</div>}
        </StepCard.Body>
      </StepCard>
    </div>
  );
}
